/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View } from 'react-native';

import * as Layout from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import ProductBadge from '@components/global/ProductBadge';
// import BranchSearch from '@components/form/search/BranchSearch';
import Table from '@components/table/Table';
// import TableMobile from '@components/table/TableMobile';
import ListStickyHeader from '@/screens/catalog/components/ListStickyHeader';
import ProductItemMobile from '@/screens/catalog/components/ProductItemMobile';
import ModalDetailProduct from '@components/transaction/catalog/ModalDetailProduct';
import BrandFilter from '@/screens/catalog/components/BrandFilter';

import { isWeb } from '@global/screens';
import { convertProductMarketType, hasProductBadge } from '@global/helpers';
import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import { paramsReseted } from '@/store/params';
import { setProductData } from '@/store/product';
import { showPopup } from '@/store/modal';
import store from '@/store';

import PropTypes from 'prop-types';

const propTypes = {
  navigation: PropTypes.object,
  activeTab: PropTypes.string,
  onChangeTab: PropTypes.func,
  mainBanner: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
};

const defaultProps = {
  navigation: {},
  activeTab: '',
  onChangeTab: () => {},
  mainBanner: false,
};

const tableStructure = {
  image: {
    head: 'Product Image',
    body: 'image',
    type: 'image',
  },
  reference: {
    head: 'Internal Reference',
    body: 'reference',
    type: 'slot',
  },
  name: {
    head: 'Name',
    body: 'name',
  },
  variant: {
    head: 'Attribute Value',
    body: 'variant',
  },
  price: {
    head: 'Price',
    body: 'price',
    type: 'currency',
  },
  attribute: {
    head: 'Attribute',
    body: 'attribute',
    type: 'slot',
  },
};

const FILTER_TYPES = [
  {
    label: 'Sales Team',
    value: 'sales_team',
  },
  {
    label: 'Regional',
    value: 'regional',
  },
];

const FILTER_TYPES_NATIONAL = [{ label: 'National', value: 'national' }];

const Product = (props) => {
  const { activeTab = '', mainBanner, onChangeTab = () => {} } = props;
  const dispatch = useDispatch();
  const paramsState = useSelector((state) => state.params);
  const productDetail = useSelector((state) => state.product.productData);
  const userData = useSelector((state) => state.auth.user);
  const userRole = userData?.role?.code;
  const userSalesTeam = userData?.sales_team?.length ? userData.sales_team : [];
  const showNational = [
    'superadmin',
    'distributor_manager',
    'general_manager',
    'vice_president',
    'general',
    'regional_distributor_manager',
  ].includes(userRole);
  const isDefaultSalesTeam = [
    'salesman',
    'sales_supervisor',
    'branch_head',
    'finance_supervisor',
    'sales_admin',
    'regional_distributor_manager',
  ].includes(userRole);
  const showRegion = [
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
  ].includes(userRole);
  const isBestSeller = activeTab == 'best-seller';

  const [selectedFilterType, setSelectedFilterType] = useState({});
  const [selectedFilter, setSelectedFilter] = useState('');
  const [selectedBrand, setSelectedBrand] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [totalData, setTotalData] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const getNewBestProducts = useApi(MsCatalog.getNewBestProducts);
  const getNewBestProductCount = useApi(MsCatalog.getNewBestProductCount);

  const HeaderFilter = useMemo(() => {
    return (
      <>
        {isBestSeller ? (
          <View
            style={[
              {
                flexDirection: isWeb ? 'row' : 'column',
              },
              isWeb
                ? {
                    zIndex: 1000,
                    elevation: 1000,
                  }
                : {},
            ]}></View>
        ) : (
          false
        )}
      </>
    );
  }, [
    isBestSeller,
    showNational,
    showRegion,
    isDefaultSalesTeam,
    userSalesTeam,
    selectedFilter,
    selectedFilterType,
    paramsState,
  ]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (!selectedFilterType?.value && !isBestSeller) {
        if (showNational) {
          setSelectedFilterType(FILTER_TYPES_NATIONAL[0]);
        } else if (showRegion) {
          setSelectedFilterType(FILTER_TYPES[1]);
        } else {
          setSelectedFilterType(FILTER_TYPES[0]);
        }
      } else {
        setSelectedFilterType({});
      }
      setSelectedFilter('');
      dispatch(paramsReseted());
      setSelectedBrand([]);
      setTotalData(0);
      setTableData([]);
    }
    return () => {
      dispatch(paramsReseted());
      mounted = false;
    };
  }, [props]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      doSearch();
    }
    return () => {
      mounted = false;
    };
  }, [selectedBrand, selectedFilter]);

  const doSearch = async (val = '', newSkip = 0) => {
    setSearchKeyword(val);
    if (!loading) {
      const isLoadMore = !isWeb && newSkip > 0;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        let selected_filter = selectedFilterType?.value;
        if (isBestSeller && !selected_filter) {
          let select_filter = {};
          if (showNational) {
            select_filter = FILTER_TYPES_NATIONAL[0];
          } else if (showRegion) {
            select_filter = FILTER_TYPES[1];
          } else {
            select_filter = FILTER_TYPES[0];
          }
          selected_filter = select_filter?.value;
          setSelectedFilterType(select_filter);
        }

        const storeParams = JSON.parse(JSON.stringify(store.getState().params));
        const payload = {
          params: {
            filter: {},
            skip: newSkip > 0 ? newSkip : storeParams.skip,
            limit: storeParams.limit,
          },
        };

        if (val || storeParams?.filter?.name) {
          const searchTerm = val || storeParams.filter.name;
          const nameRegex = searchTerm?.['$regex']
            ? searchTerm
            : { $regex: searchTerm, $options: 'i' };
          payload.params.filter['$or'] = [
            { 'product.name': nameRegex },
            { 'brand.name': nameRegex },
            { 'combination.reference': nameRegex },
          ];
        }

        if (selectedBrand?.length) {
          payload.params.filter['brand.id'] = {
            $in: selectedBrand.map((a) => a.id),
          };
        }

        // if (isBestSeller) {
        //   payload.params.filter['best_seller_scope'] = selected_filter;
        //   if (
        //     selected_filter == 'sales_team' &&
        //     (selectedFilter?.id || isDefaultSalesTeam)
        //   ) {
        //     if (isDefaultSalesTeam) {
        //       payload.params.filter['best_seller_sales_team.id'] = {
        //         $in: userSalesTeam.map((s) => s.id),
        //       };
        //     } else {
        //       payload.params.filter['best_seller_sales_team.id'] =
        //         selectedFilter?.id;
        //     }
        //   } else if (selected_filter == 'regional' && selectedFilter?.value) {
        //     payload.params.filter['best_seller_region'] = selectedFilter?.value;
        //   }
        // } else {
        //   if (selectedBrand?.length) {
        //     payload.params.filter['brand.id'] = {
        //       $in: selectedBrand.map((a) => a.id),
        //     };
        //   }
        //   // if (storeParams?.filter?.name) {
        //   //   payload.params.filter['product.name'] = storeParams?.filter?.name;
        //   // }
        // }

        // if (isBestSeller && showRegion && selected_filter == 'regional') {
        //   if (userData?.branch?.[0]?.id) {
        //     const regionDetail = await MsAccount.getBranchDetail(
        //       userData.branch[0].id,
        //     );
        //     if (regionDetail?.data?.data?.regional) {
        //       const value = regionDetail.data.data.regional;
        //       const region = REGIONAL.find((r) => r.value == value);
        //       payload.params.filter['best_seller_region'] = value;
        //       setSelectedFilter(region);
        //     } else {
        //       if (isLoadMore) {
        //         setLoadingMore(false);
        //       } else {
        //         setLoading(false);
        //       }
        //       return;
        //     }
        //   } else {
        //     if (isLoadMore) {
        //       setLoadingMore(false);
        //     } else {
        //       setLoading(false);
        //     }
        //     return;
        //   }
        // }

        const productType = isBestSeller ? 'best_seller' : 'new_arrival';

        if (payload?.params?.skip === 0) {
          const countResponse = await getNewBestProductCount.request(
            productType,
            payload,
          );
          if (countResponse && countResponse.data && countResponse.data.data) {
            setTotalData(countResponse.data.data);
          } else {
            setTotalData(0);
          }
        }

        const response = await getNewBestProducts.request(productType, payload);
        if (response?.data?.data?.length) {
          const result = response.data.data.map((pr) => {
            return convertProductMarketType(pr);
          });
          // setTotalData(result.length);
          if (isLoadMore) {
            const last_result = tableData.concat(result);
            setTableData(last_result);
          } else {
            setTableData(result);
          }
        } else {
          // setTotalData(0);
          if (!isLoadMore) {
            setTableData([]);
          }
        }
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      } catch (e) {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
        console.log(e);
      }
    }
  };

  const gotToDetail = (data) => {
    dispatch(setProductData(data));
  };

  const onCloseDetail = () => {
    dispatch(setProductData({}));
  };

  return (
    <>
      {isWeb ? (
        <Table
          name={isBestSeller ? 'catalog-best-seller' : 'catalog-new-arrival'}
          structures={tableStructure}
          data={tableData}
          loading={loading}
          hasCallback
          hasNoHide
          noRowColor
          rowBottomBorder
          showFilter={isBestSeller ? false : true}
          showSearch={isBestSeller ? false : true}
          totalData={totalData}
          skip={paramsState.skip}
          limit={paramsState.limit}
          searchKey="name"
          placeholder="Search by name"
          slot={{
            reference: ({ data, index }) => (
              <SociollaText style={{ fontSize: 13 }} key={`reference-${index}`}>
                {data?.reference ? `[${data.reference}]` : '-'}
              </SociollaText>
            ),
            attribute: ({ data, index }) => (
              <View
                style={{ flexDirection: 'row', flexWrap: 'wrap' }}
                key={`slot-attribute-${index}`}>
                {data.is_best_seller ? (
                  <ProductBadge status="best" style={{ marginLeft: 5 }} />
                ) : (
                  false
                )}
                {data.is_promo ? (
                  <ProductBadge status="promo" style={{ marginLeft: 5 }} />
                ) : (
                  false
                )}
                {data.is_new ? (
                  <ProductBadge status="new" style={{ marginLeft: 5 }} />
                ) : (
                  false
                )}
                {!hasProductBadge(data) ? (
                  <SociollaText>-</SociollaText>
                ) : (
                  false
                )}
              </View>
            ),
          }}
          onClickRow={({ data }) => gotToDetail(data)}
          onSearch={(value) => doSearch(value)}
          moreInlineFilter={HeaderFilter}
        />
      ) : (
        <ListStickyHeader
          name={isBestSeller ? 'catalog-best-seller' : 'catalog-new-arrival'}
          searchKey="name"
          placeholder="Search by name"
          loading={loading}
          onSearch={(value) => doSearch(value)}
          data={tableData}
          showFilterMobile
          hasFilter={Boolean(selectedBrand?.length)}
          headerComponent={HeaderFilter}
          showSearch={!isBestSeller}
          activeTab={activeTab}
          withLoadMore
          withFetch
          loadingPagination={loadingMore}
          totalData={totalData}
          skip={paramsState.skip}
          limit={paramsState.limit}
          onChangeTab={(val) => onChangeTab(val)}
          onLoadMore={(newSkip) => doSearch(searchKeyword, newSkip)}
          mainBanner={mainBanner}
          renderItem={({ data, index }) => (
            <ProductItemMobile
              data={data}
              onCallback={() => gotToDetail(data)}
              key={index}
            />
          )}
        />
      )}
      <ModalDetailProduct
        back={!isWeb}
        withPromotion
        title="Product Detail"
        show={Boolean(productDetail && Object.keys(productDetail).length)}
        onClose={() => onCloseDetail()}
      />
      {!isBestSeller ? (
        <Layout.Filter
          name="catalog-new-arrival"
          isLocalVariable
          hasFilterData={Boolean(selectedBrand?.length)}
          onReset={() => {
            // doSearch();
            setSelectedBrand([]);
          }}
          onApply={() => {
            // doSearch();
            dispatch(showPopup({ name: 'showFilter', value: false }));
          }}>
          <BrandFilter
            selected={selectedBrand}
            onSelect={(val) => setSelectedBrand(val)}
          />
        </Layout.Filter>
      ) : (
        false
      )}
    </>
  );
};

Product.propTypes = propTypes;
Product.defaultProps = defaultProps;

export default React.memo(Product, (prev, next) => prev == next);
